import { Badge, Button, Group, Paper, Stack, Text, ThemeIcon } from "@mantine/core"
import { useEffect, useState } from "react"
import { apiFetch } from "atlas_forecasting_app/lib/api_fetch"
import { Carousel } from "atlas_forecasting_app/components/common/carousel"
import { AuthModalButton, EmptyState, ErrorAlert, LoadingState, PageHeader, SectionCard } from "atlas_forecasting_app/components/common/page_elements"
import { IconChartDots, IconCirclePlus, IconShieldCheck, IconUserPlus, IconWallet, appIcon } from "atlas_forecasting_app/components/common/icons"
import { RouterLink } from "atlas_forecasting_app/lib/router"

const CAPABILITY_ITEMS = [
  {
    icon: IconCirclePlus,
    title: "Guided question drafting",
    body: "Refine a forecasting question through a structured AI-assisted clarification flow before you confirm it.",
  },
  {
    icon: IconChartDots,
    title: "Public and private forecasting",
    body: "Keep local work private by default, or publish questions and forecasts when you want broader visibility.",
  },
  {
    icon: IconWallet,
    title: "Secure Atlas balance funding",
    body: "Add funds through Stripe, then spend your Atlas balance on AI-generated forecasts.",
  },
]

const GET_STARTED_STEPS = [
  {
    step: "1",
    title: "Create your account",
    body: "Sign up or log in with Google to access private question drafting, account settings, and forecast history.",
  },
  {
    step: "2",
    title: "Create or import a question",
    body: "Draft a new forecasting question with Atlas, or import one from sources like Metaculus or Kalshi.",
  },
  {
    step: "3",
    title: "Fund and forecast",
    body: "Add funds through Stripe, request a forecast, and review how the result evolves over time.",
  },
]

export function HomePage({ currentUser, authInfo, onAuthResult }) {
  const [featured, setFeatured] = useState([])
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let mounted = true
    setLoading(true)
    apiFetch("/api/public/featured_questions")
      .then((data) => {
        if (!mounted) return
        setFeatured(data.items || [])
        setError("")
      })
      .catch((e) => mounted && setError(e.message))
      .finally(() => mounted && setLoading(false))
    return () => {
      mounted = false
    }
  }, [])

  const scrollToFeaturedQuestions = () => {
    const featuredQuestionsSection = document.getElementById("featured-questions")

    if (!featuredQuestionsSection) return

    const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false

    featuredQuestionsSection.scrollIntoView({
      block: "start",
      behavior: prefersReducedMotion ? "auto" : "smooth",
    })
  }

  const primaryHeroAction = currentUser ? (
    <Button component={RouterLink} to="/feed" size="lg">
      Go to forecast feed
    </Button>
  ) : authInfo?.google_client_id ? (
    <AuthModalButton
      authInfo={authInfo}
      onAuthResult={onAuthResult}
      label="Sign up or Log in"
      size="lg"
      modalTitle="Sign up or Log in"
      modalBody="Choose how you'd like to access Atlas Forecasting."
      leftSection={appIcon(IconUserPlus, "inline")}
    />
  ) : (
    <Button size="lg" disabled>
      Log in unavailable
    </Button>
  )

  const secondaryHeroAction = currentUser ? (
    <Button component={RouterLink} to="/new-question" variant="light" size="lg">
      Create a question
    </Button>
  ) : (
    <Button type="button" onClick={scrollToFeaturedQuestions} variant="light" size="lg">
      Explore featured questions
    </Button>
  )

  return (
    <Stack gap="xl">
      <Paper withBorder radius="xl" p="xl" className="af-home-hero">
        <Stack gap="xl">
          <PageHeader
            eyebrow="Atlas Forecasting"
            variant="hero"
            title="Better decisions start with better forecasts"
            subtitle="Create well-specified questions, generate probabilistic forecasts, and review how those forecasts change over time."
            action={
              <Group gap="sm" className="af-home-hero__actions">
                {primaryHeroAction}
                {secondaryHeroAction}
              </Group>
            }
          />

          <div className="af-home-capability-strip">
            {CAPABILITY_ITEMS.map(({ icon: Icon, title, body }) => (
              <Paper key={title} withBorder radius="xl" p="lg" className="af-home-capability-card">
                <Stack gap="sm">
                  <ThemeIcon variant="light" color="blue" radius="xl" size={42}>
                    {appIcon(Icon, "page")}
                  </ThemeIcon>
                  <Text fw={700}>{title}</Text>
                  <Text size="sm" c="dimmed">
                    {body}
                  </Text>
                </Stack>
              </Paper>
            ))}
          </div>
        </Stack>
      </Paper>

      <SectionCard
        id="featured-questions"
        className="af-home-featured-section"
        variant="feature"
        title="Featured questions"
        subtitle="Public questions Atlas is actively monitoring."
        action={
          <Badge variant="light" color="blue" size="lg">
            Public
          </Badge>
        }
      >
        {loading ? <LoadingState label="Loading featured questions..." /> : null}
        <ErrorAlert message={error} />
        {!loading && !error && featured.length === 0 ? <EmptyState title="Featured questions are coming soon" message="Atlas has not published any featured questions yet." variant="soft" /> : null}
        {featured.length > 0 ? <Carousel items={featured} /> : null}
      </SectionCard>

      <SectionCard
        variant="editorial"
        title="How Atlas works"
        subtitle="A focused workflow for building, funding, and tracking forecasts."
      >
        <div className="af-home-steps">
          {GET_STARTED_STEPS.map(({ step, title, body }) => (
            <Paper key={step} withBorder radius="xl" p="lg" className="af-home-step-card">
              <Stack gap="sm">
                <Text className="af-home-step-card__step">{step}</Text>
                <Text fw={700}>{title}</Text>
                <Text size="sm" c="dimmed">
                  {body}
                </Text>
              </Stack>
            </Paper>
          ))}
        </div>

        {!currentUser ? (
          authInfo?.google_client_id ? (
            <AuthModalButton
              authInfo={authInfo}
              onAuthResult={onAuthResult}
              label="Sign up or Log in"
              modalTitle="Sign up or Log in"
              modalBody="Choose how you'd like to access Atlas Forecasting."
              leftSection={appIcon(IconUserPlus, "inline")}
            />
          ) : (
            <ErrorAlert message="Google sign-in is not configured (set GOOGLE_CLIENT_ID)." />
          )
        ) : (
          <Group gap="sm">
            <Button component={RouterLink} to="/new-question">
              Start a new question
            </Button>
            <Button component={RouterLink} to="/my-questions" variant="light">
              Review my questions
            </Button>
          </Group>
        )}

        <Paper withBorder radius="xl" p="lg" className="af-home-assurance-card">
          <Group align="flex-start" gap="sm" wrap="nowrap">
            <ThemeIcon variant="light" color="blue" radius="xl" size={40}>
              {appIcon(IconShieldCheck, "page")}
            </ThemeIcon>
            <Stack gap={4}>
              <Text fw={700}>A more disciplined forecasting workflow</Text>
              <Text size="sm" c="dimmed">
                Atlas is designed for people who want clearer questions, defensible forecasts, and a better record of how those forecasts evolve.
              </Text>
            </Stack>
          </Group>
        </Paper>
      </SectionCard>
    </Stack>
  )
}
